import { useCallback, useEffect, useState } from "react";
import { api, Unauthorised, type Board, type Today as TodayData } from "./api.ts";
import { C, mono, sans, space, type, radius, border, longDate, todayISO } from "./theme.ts";
import { useTheme } from "./glassTheme.tsx";
import { Blobs } from "./components/glass/Glass.tsx";
import { Nav, type Module } from "./components/glass/Nav.tsx";
import { Sessions } from "./views/Sessions.tsx";
import { Board as BoardView } from "./views/Board.tsx";
import { Search, emptySearch, type SearchState } from "./views/Search.tsx";
import { Graph } from "./views/Graph.tsx";
import { Collections } from "./views/Collections.tsx";
import { Calendar } from "./views/Calendar.tsx";
import { Money } from "./views/Money.tsx";
import { Docs } from "./views/Docs.tsx";
import { Pantry } from "./views/Pantry.tsx";
import { Settings } from "./views/Settings.tsx";
import { Assistant } from "./components/Assistant.tsx";
import { Build } from "./views/Build.tsx";
import { Workout, type WorkoutTarget } from "./views/Workout.tsx";
import { TaskDetail, type DetailMode } from "./components/board/TaskDetail.tsx";
import { PullToRefresh } from "./components/PullToRefresh.tsx";
import { BottomSheet } from "./components/BottomSheet.tsx";
import { MenuItem } from "./components/MenuItem.tsx";
import { Lock as LockIcon } from "./components/icons.tsx";
import { Button } from "./components/Button.tsx";
import { useCloseOnBack } from "./useCloseOnBack.ts";

type Tab = "today" | "board" | "search" | "calendar" | "money" | "graph" | "collections" | "workout" | "pantry" | "docs" | "settings" | "build";

/**
 * Session 18: the six-module glass nav pill (`Nav`) replaces this bar
 * (Sessions/Board/Money/Body/Journal/Calendar). Board is rebuilt now, along
 * with the shared task detail sheet it owns (`TaskDetail`, replacing the old
 * `Detail`) — used from every tab that opens a task, not only Board. Two of
 * the six still point at old-styled screens: Body at `pantry`, Journal at
 * `docs` — see `TAB_FOR_MODULE` below. What's left sits behind a long press
 * on the pill instead of a tab row: Settings per the spec's own placement,
 * and Graph/Ideas/Workout/Build, which the six modules don't have a home
 * for yet.
 */
const MENU: { key: Tab; label: string; note: string }[] = [
  { key: "graph", label: "Graph", note: "the board as a tree" },
  { key: "collections", label: "Ideas", note: "kept, with no obligation" },
  // Read-only, like Docs, and for the same reason: the three documents it
  // renders are edited from a chat, through the connector.
  { key: "workout", label: "Workout", note: "the tiers, the cores, the cueing" },
  { key: "settings", label: "Settings", note: "the model, and what it costs" },
  { key: "build", label: "Build", note: "the food and training system" },
];

const TAB_FOR_MODULE: Record<Module, Tab> = {
  sessions: "today", board: "board", money: "money", body: "pantry", journal: "docs", calendar: "calendar",
};
const MODULE_FOR_TAB: Partial<Record<Tab, Module>> = {
  today: "sessions", board: "board", money: "money", pantry: "body", docs: "journal", calendar: "calendar",
};

/**
 * `Build` is in the menu only while the area it describes still has something
 * open. When the last task closes the entry goes on its own — that is the
 * whole of "temporary". There is no cleanup task, no expiry date and no flag,
 * because all three are things that have to be remembered.
 */
const BUILD_AREA = "food-training";
const buildIsLive = (board: Board | null) =>
  !!board?.tasks.some((t) => t.area_id === BUILD_AREA && t.state !== "done" && t.state !== "parked");

export default function App() {
  const [authed, setAuthed] = useState<boolean | null>(null);
  const [tab, setTab] = useState<Tab>("today");
  const [board, setBoard] = useState<Board | null>(null);
  const [today, setToday] = useState<TodayData | null>(null);
  const [detail, setDetail] = useState<DetailMode | null>(null);
  const [boardValueFilter, setBoardValueFilter] = useState<string | null>(null);
  // Held here, not in Search, so the query and its filters are still there
  // after a trip to another tab — and after the task sheet, which opens over
  // Search rather than replacing it.
  const [search, setSearch] = useState<SearchState>(emptySearch);
  const [error, setError] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [askOpen, setAskOpen] = useState(false);
  // Set only by a task's "Dive in" — pre-fills the Assistant's draft once,
  // then is cleared on close so reopening "Ask AI" later doesn't carry a
  // stale task reference into an unrelated conversation.
  const [diveInSeed, setDiveInSeed] = useState<string | null>(null);
  const { colors } = useTheme();
  const openTask = (id: string) => setDetail({ kind: "edit", id });
  // Where the Workout view opens when reached from the Training slot's detail
  // sheet: the variant the slot says is next. Cleared when the tab is left, so
  // opening Workout from the menu later starts at its index, not wherever the
  // slot last pointed. Not persisted anywhere.
  const [workoutTarget, setWorkoutTarget] = useState<WorkoutTarget | null>(null);
  const openWorkout = (variant: 1 | 2) => { setWorkoutTarget({ variant }); setTab("workout"); setMenuOpen(false); };
  useEffect(() => { if (tab !== "workout") setWorkoutTarget(null); }, [tab]);

  const load = useCallback(async () => {
    try {
      const [b, t] = await Promise.all([api.board(), api.today()]);
      setBoard(b); setToday(t); setError(null);
    } catch (e) {
      if (e instanceof Unauthorised) { setAuthed(false); return; }
      setError((e as Error).message);
    }
  }, []);

  useEffect(() => { api.me().then((r) => setAuthed(r.authenticated)).catch(() => setAuthed(false)); }, []);
  useEffect(() => { if (authed) load(); }, [authed, load]);

  const buildLive = buildIsLive(board);

  // Standing on the Build tab when the last of its tasks closes. The entry has
  // just disappeared from the menu, so the tab is no longer reachable and
  // should not be what is on screen either.
  useEffect(() => {
    if (tab === "build" && board && !buildLive) setTab("today");
  }, [tab, board, buildLive]);

  const menu = MENU.filter((m) => m.key !== "build" || buildLive);

  const activeModule = MODULE_FOR_TAB[tab] ?? null;
  const goSearch = () => { setTab("search"); setMenuOpen(false); };
  const navigate = (m: Module) => {
    setTab(TAB_FOR_MODULE[m]);
    setMenuOpen(false);
  };
  // Board's +add opens the detail sheet empty, pre-filled with whatever
  // value Board is currently filtered to (§2). The other four non-Sessions
  // modules don't have an add sheet built yet, so their +add is still a
  // structural placeholder.
  const fab = activeModule === "board"
    ? { onClick: () => setDetail({ kind: "create", value_id: boardValueFilter, area_id: null }) }
    : activeModule && activeModule !== "sessions" ? { onClick: () => {} } : null;

  const lock = async () => { await api.logout(); setAuthed(false); };

  if (authed === null) return <Shell><p style={{ color: C.inkFaint, padding: space.huge }}>Opening the board…</p></Shell>;
  if (!authed) return <Login onIn={() => setAuthed(true)} />;

  return (
    <Shell>
      {/* The header and the open view come down with the pull; everything
          below — the sheet, the assistant, the menu, the bar — is fixed and
          stays outside it. */}
      <PullToRefresh onRefresh={load} enabled={!detail && !menuOpen} background={colors.ground}>
      {error && <p style={{ color: C.alert, fontSize: type.body, padding: `${space.xxl}px ${space.xxl}px 0` }}>{error}</p>}
      {tab === "today" || tab === "board" || tab === "search" ? (
        // Sessions, Board and Search own their own headers as part of their
        // content column — the cockpit's shared header below doesn't apply here.
        // Until the rest of the rebuild reaches them, the other tabs' old
        // header is a deliberate visual jump, not an oversight.
        today && board && (
          tab === "today"
            ? <Sessions data={today} board={board} onOpen={openTask} onChanged={load} onOpenWorkout={openWorkout} />
            : tab === "search"
            ? <Search board={board} state={search} onState={setSearch} onOpen={openTask} onOpenIdeas={() => setTab("collections")} />
            : <BoardView board={board} onOpen={openTask} onChanged={load} valueFilter={boardValueFilter} onValueFilterChange={setBoardValueFilter} />
        )
      ) : (
        <>
          <header style={{ padding: `${space.xxxl}px ${space.xxl}px ${space.md}px` }}>
            <div style={{ fontFamily: mono, fontSize: type.small, color: C.inkFaint, letterSpacing: 1.4 }}>MISSION CONTROL</div>
            <h1 style={{ fontSize: type.display, fontWeight: 300, margin: "3px 0 0" }}>{longDate(today?.date ?? todayISO())}</h1>
          </header>

          {tab === "calendar" && <Calendar />}
          {tab === "money" && <Money onOpen={openTask} />}
          {tab === "graph" && board && <Graph board={board} onOpen={openTask} />}
          {tab === "collections" && board && <Collections board={board} onChanged={load} onOpen={openTask} />}
          {tab === "workout" && <Workout target={workoutTarget} />}
          {tab === "pantry" && <Pantry />}
          {tab === "docs" && <Docs />}
          {tab === "settings" && <Settings />}
          {tab === "build" && buildLive && board && <Build board={board} onOpen={openTask} />}
        </>
      )}
      </PullToRefresh>

      {/* The task sheet has never answered the Android back gesture, which
          from a PWA meant leaving the app. Mounted once for as long as any
          task is open — not keyed by id — so following a blocker from one
          task to another is not mistaken for a back press. */}
      {detail && <BackClosesSheet onClose={() => setDetail(null)} />}
      {detail && board && (
        <TaskDetail
          // A fresh sheet every time what's open changes identity — without
          // this, switching from one failed fetch straight into +add (or
          // into another task) reuses the mounted instance and its stale
          // `error`/`t` state along with it.
          key={detail.kind === "edit" ? detail.id : "create"}
          mode={detail} board={board}
          onClose={() => setDetail(null)} onChanged={load} onOpen={openTask}
          onDiveIn={(t) => {
            setDetail(null);
            setDiveInSeed(`Let's dig into ${t.id}: ${t.title}.`);
            setAskOpen(true);
          }}
        />
      )}

      {/* Opened from "Ask AI" in the nav menu, over whatever tab is open — the
          assistant reads the whole board, so it does not belong to a screen.
          Not offered while a task detail sheet is open, which already owns
          the bottom-right corner. */}
      {!detail && askOpen && (
        <Assistant seed={diveInSeed ?? undefined} onClose={() => { setAskOpen(false); setDiveInSeed(null); }} />
      )}

      {menuOpen && (
        <BottomSheet
          onClose={() => setMenuOpen(false)}
          zIndex={35}
          topBorder={{ width: border.hairline, colour: C.rule }}
          padding={`${space.lg}px ${space.xxl}px calc(${space.xxxl}px + env(safe-area-inset-bottom))`}
          scroll
        >
          <MenuItem
            label="Ask AI" note="asks before it changes anything" active={askOpen} noBorder
            onClick={() => { setAskOpen(true); setMenuOpen(false); }}
          />
          <div style={{ borderTop: `${border.hairline}px solid ${C.ruleStrong}`, margin: `${space.sm}px 0 ${space.xs}px` }} />

          {menu.map(({ key, label, note }) => (
            <MenuItem key={key} label={label} note={note} active={tab === key} onClick={() => { setTab(key); setMenuOpen(false); }} />
          ))}

          <div style={{ borderTop: `${border.hairline}px solid ${C.ruleStrong}`, margin: `${space.sm}px 0 ${space.xs}px` }} />
          <MenuItem
            label="Lock" note="back to the passphrase" active={false} noBorder
            icon={<LockIcon size={16} strokeWidth={1.6} color={C.inkFaint} />}
            onClick={() => { setMenuOpen(false); lock(); }}
          />
        </BottomSheet>
      )}

      <Nav
        active={activeModule}
        onNavigate={navigate}
        onLongPress={() => setMenuOpen(true)}
        onSearch={goSearch}
        searchActive={tab === "search"}
        fab={fab}
      />
    </Shell>
  );
}

function BackClosesSheet({ onClose }: { onClose: () => void }) {
  useCloseOnBack(onClose);
  return null;
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    // `isolation: isolate` gives this its own stacking context, so `Blobs`'s
    // negative z-index is resolved only against what's inside here. Without
    // it, index.html's own flat `<body>` background — a separate painted
    // box, not the transparent canvas colour — sits in front of a negative
    // z-index descendant of `body`, and the ground never shows.
    <div style={{
      isolation: "isolate", fontFamily: sans, color: C.ink, minHeight: "100dvh",
      boxSizing: "border-box", maxWidth: 560, margin: "0 auto",
      // Clears the status bar and the camera cutout at the top, and the pill
      // plus the +/search row above it at the bottom. All three are 0 where
      // the device reserves that space itself.
      paddingTop: "env(safe-area-inset-top)",
      paddingBottom: "calc(var(--nav-clear) + 58px)",
      paddingLeft: "env(safe-area-inset-left)", paddingRight: "env(safe-area-inset-right)",
    }}>
      <Blobs />
      {children}
    </div>
  );
}

function Login({ onIn }: { onIn: () => void }) {
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try { await api.login(value); onIn(); }
    catch (err: any) { setError(err.message); }
    finally { setBusy(false); }
  };

  return (
    <Shell>
      <div style={{ padding: `22dvh ${space.huge}px 0` }}>
        <div style={{ fontFamily: mono, fontSize: type.small, color: C.inkFaint, letterSpacing: 1.4 }}>MISSION CONTROL</div>
        <form onSubmit={submit} style={{ marginTop: space.xxl }}>
          <input
            type="password" autoFocus value={value} onChange={(e) => setValue(e.target.value)}
            placeholder="passphrase" autoComplete="current-password"
            style={{
              width: "100%", boxSizing: "border-box", fontFamily: sans, fontSize: type.input,
              padding: `${space.ml}px ${space.lg}px`, borderRadius: radius.lg, border: `${border.hairline}px solid ${C.ruleStrong}`, background: C.card,
            }}
          />
          <Button
            variant="primary" fullWidth htmlType="submit" disabled={busy || !value}
            style={{ marginTop: space.md, opacity: value ? 1 : 0.4 }}
          >{busy ? "…" : "Open"}</Button>
        </form>
        {error && <p style={{ color: C.alert, fontSize: type.body, marginTop: space.ml }}>{error}</p>}
      </div>
    </Shell>
  );
}
